import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import {
  JsonApiResponseDto,
  Response,
  TQueryParams,
  formatQueryParams,
  parseJsonApiResponse,
  prepareHeaders,
} from 'evolve-ui';
import { TAgency } from '../..';

export type TUser = {
  id: number;
  name: string;
  phone?: string;
  cellphone?: string;
  email: string;
  acronym: string;
  location?: string;
  active: boolean;
  agency: TAgency;
  category?: string;
  category_en?: string;
  profile?: {
    id: number;
    parentId?: number;
    name: string;
  };
  agencies?: TAgency[];
};

export const usersApi = createApi({
  reducerPath: 'users',
  baseQuery: fetchBaseQuery({
    baseUrl: `${process.env.GATSBY_USERS_API_URL}`,
    mode: 'cors',
    prepareHeaders,
  }),
  tagTypes: ['User'],
  endpoints: (builder) => ({
    getUsers: builder.query<Response<TUser>, TQueryParams>({
      providesTags: ['User'],
      query: formatQueryParams,
      transformResponse: parseJsonApiResponse<TUser>,
    }),
    getUsersWithAgencies: builder.query<Response<TUser>, TQueryParams>({
      providesTags: ['User'],
      query: (params) => formatQueryParams(params, 'v2'),
      transformResponse: (res, meta, args) => {
        return parseJsonApiResponse<TUser>(
          res as JsonApiResponseDto<TUser>,
          meta,
          args,
          true,
        );
      },
    }),
    getUser: builder.query<Response<TUser>, TQueryParams & { userId: string }>({
      providesTags: ['User'],
      query: ({ userId, ...params }) =>
        formatQueryParams(params, `v2/${userId}`),
      transformResponse: parseJsonApiResponse<TUser>,
    }),
    addAgencyToUser: builder.mutation<
      Response<TUser>,
      { userId: string; agencyId: number }
    >({
      invalidatesTags: ['User'],
      query: ({ userId, agencyId }) => ({
        url: `v2/${userId}/agencies`,
        method: 'POST',
        body: { agencyId },
      }),
      transformResponse: parseJsonApiResponse<TUser>,
    }),
    removeAgencyFromUser: builder.mutation<
      Response<TUser>,
      { userId: string; agencyId: number }
    >({
      invalidatesTags: ['User'],
      query: ({ userId, agencyId }) => ({
        url: `v2/${userId}/agencies/${agencyId}`,
        method: 'DELETE',
      }),
    }),
    removeUser: builder.mutation<Response<TUser>, { userId: string | number }>({
      invalidatesTags: ['User'],
      query: ({ userId }) => ({
        url: `v2/${userId}`,
        method: 'DELETE',
      }),
    }),
  }),
});

export const {
  useGetUsersQuery,
  useGetUsersWithAgenciesQuery,
  useGetUserQuery,
  useAddAgencyToUserMutation,
  useRemoveAgencyFromUserMutation,
  useRemoveUserMutation,
} = usersApi;
